import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate, useParams } from "react-router-dom";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";

import SoftButton from "components/SoftButton";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import { showSuiErrorSnackbar } from "components/SuiSnackbar";
import SoftTypography from "components/SoftTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { GraphqlRequestError } from "apollo/graphql-request";
import { useAuth } from "auth";
import { useLocale } from "lib/i18n/client";
import { getTranslations, interpolateTemplate } from "lib/i18n/dictionary";
import { CitiesAutocomplete } from "profile/cities-autocomplete";
import {
  buildServiceCitiesPayload,
  confirmServiceCitiesReset,
  createServiceCitiesState,
  deleteServiceRequest,
  fetchServiceByUuid,
  readCachedServiceByUuid,
  removeServiceMediaRequest,
  rehostServiceMediaRequest,
  reorderServiceMediasRequest,
  requestSameAsProfileChange,
  resolveVisibleServiceCities,
  SERVICE_SLUG_TAKEN_ERROR_CODE,
  type ServiceCitiesState,
  type ServiceCity,
  type ServiceMedia,
  type ServiceMediaType,
  type ServicePriceCurrency,
  type ServicePriceMode,
  type ServicePriceUnit,
  updateOwnServiceCities,
  uploadServiceMediaRequest,
  updateServiceMediaRequest,
  updateServiceRequest,
} from "service";
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY_CODE } from "currency/currency";

const MEDIA_TYPE_OPTIONS: Array<{ value: ServiceMediaType; label: string }> = [
  { value: "IMAGE", label: "Image" },
  { value: "VIDEO", label: "Video" },
  { value: "MUSIC", label: "Music" },
  { value: "FILE", label: "File" },
];
type MediaSourceMode = "FILE" | "URL";
// Mirrors MAX_SERVICE_MEDIA_UPLOAD_SIZE_BYTES in apps/api/src/modules/media/media.constants.ts.
const MAX_MEDIA_UPLOAD_SIZE_BYTES = 1024 * 1024 * 1024;

function getMediaTypeLabel(mediaType: ServiceMediaType): string {
  const mediaTypeOption = MEDIA_TYPE_OPTIONS.find((option) => option.value === mediaType);
  return mediaTypeOption?.label ?? mediaType;
}

function detectMediaTypeFromFile(file: File | null): ServiceMediaType | null {
  if (!file) {
    return null;
  }

  const normalizedMimeType = file.type.trim().toLowerCase();

  if (normalizedMimeType.startsWith("image/")) {
    return "IMAGE";
  }

  if (normalizedMimeType.startsWith("video/")) {
    return "VIDEO";
  }

  if (normalizedMimeType.startsWith("audio/")) {
    return "MUSIC";
  }

  if (normalizedMimeType.startsWith("text/") || normalizedMimeType.startsWith("application/")) {
    return "FILE";
  }

  const fileExtension = file.name.trim().split(".").pop()?.toLowerCase();
  if (!fileExtension) {
    return null;
  }

  if (
    ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic", "heif", "avif"].includes(
      fileExtension
    )
  ) {
    return "IMAGE";
  }

  if (["mp4", "mov", "avi", "mkv", "webm", "m4v", "wmv", "flv"].includes(fileExtension)) {
    return "VIDEO";
  }

  if (["mp3", "wav", "ogg", "aac", "m4a", "flac", "opus"].includes(fileExtension)) {
    return "MUSIC";
  }

  if (
    [
      "pdf",
      "txt",
      "csv",
      "json",
      "xml",
      "zip",
      "rar",
      "7z",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "ppt",
      "pptx",
    ].includes(fileExtension)
  ) {
    return "FILE";
  }

  return null;
}

function getMediaIcon(mediaType: ServiceMediaType): string {
  if (mediaType === "VIDEO") {
    return "smart_display";
  }

  if (mediaType === "MUSIC") {
    return "music_note";
  }

  if (mediaType === "FILE") {
    return "insert_drive_file";
  }

  return "image";
}

function sortServiceMedias(medias: ServiceMedia[]): ServiceMedia[] {
  return [...medias].sort((firstMedia, secondMedia) => {
    if (firstMedia.sortOrder === secondMedia.sortOrder) {
      return firstMedia.uuid.localeCompare(secondMedia.uuid);
    }

    return firstMedia.sortOrder - secondMedia.sortOrder;
  });
}

function moveServiceMedia(
  medias: ServiceMedia[],
  fromIndex: number,
  toIndex: number
): ServiceMedia[] {
  const reorderedMedias = [...medias];
  const [movedMedia] = reorderedMedias.splice(fromIndex, 1);
  if (!movedMedia) {
    return medias;
  }

  reorderedMedias.splice(toIndex, 0, movedMedia);

  return reorderedMedias.map((media, index) => ({
    ...media,
    sortOrder: index,
  }));
}

function hasSameMediaOrder(firstMedias: ServiceMedia[], secondMedias: ServiceMedia[]): boolean {
  if (firstMedias.length !== secondMedias.length) {
    return false;
  }

  return firstMedias.every((media, index) => media.uuid === secondMedias[index]?.uuid);
}

function resolveLocalizedServiceUpdateErrorMessage(
  error: unknown,
  slugTakenMessage: string
): string | null {
  if (!(error instanceof GraphqlRequestError) || !error.code) {
    return null;
  }

  return error.code === SERVICE_SLUG_TAKEN_ERROR_CODE ? slugTakenMessage : null;
}

function EditService() {
  const navigate = useNavigate();
  const { serviceUuid = "" } = useParams();
  const { accessToken } = useAuth();
  const locale = useLocale();
  const translations = useMemo(() => getTranslations(locale), [locale]);
  const serviceEditorTranslations = translations.adminServiceEditor;
  const cachedService = serviceUuid.trim() ? readCachedServiceByUuid(serviceUuid) : undefined;

  const [isLoading, setIsLoading] = useState(() => cachedService === undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [description, setDescription] = useState("");
  const [priceMode, setPriceMode] = useState<ServicePriceMode>("FIXED");
  const [priceCurrency, setPriceCurrency] = useState<ServicePriceCurrency>(DEFAULT_CURRENCY_CODE);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [priceUnit, setPriceUnit] = useState<ServicePriceUnit | "">("");
  const [priceUnitCustom, setPriceUnitCustom] = useState("");
  const [medias, setMedias] = useState<ServiceMedia[]>([]);
  const [activeLightboxIndex, setActiveLightboxIndex] = useState(-1);
  const [isAddMediaDialogOpen, setIsAddMediaDialogOpen] = useState(false);
  const [isMediaSubmitting, setIsMediaSubmitting] = useState(false);
  const [isMediaUpdating, setIsMediaUpdating] = useState(false);
  const [mediaUuidPendingRemoval, setMediaUuidPendingRemoval] = useState<string | null>(null);
  const [mediaSourceMode, setMediaSourceMode] = useState<MediaSourceMode>("FILE");
  const [mediaSourceUrl, setMediaSourceUrl] = useState("");
  const [mediaType, setMediaType] = useState<ServiceMediaType | "">("");
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaAltDrafts, setMediaAltDrafts] = useState<Record<string, string>>({});
  const [mediaErrorMessage, setMediaErrorMessage] = useState("");
  const [mediaUploadErrorMessage, setMediaUploadErrorMessage] = useState("");
  const [draggedMediaUuid, setDraggedMediaUuid] = useState<string | null>(null);
  const [dragOverMediaUuid, setDragOverMediaUuid] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [citiesState, setCitiesState] = useState<ServiceCitiesState>(() =>
    createServiceCitiesState()
  );
  const [isCitiesResetDialogOpen, setIsCitiesResetDialogOpen] = useState(false);
  const quickAddFileInputRef = useRef<HTMLInputElement | null>(null);
  const dragStartMediasRef = useRef<ServiceMedia[] | null>(null);
  const currentMediasRef = useRef<ServiceMedia[]>([]);
  const didDropMediaRef = useRef(false);

  useEffect(() => {
    if (errorMessage) {
      showSuiErrorSnackbar(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (mediaErrorMessage) {
      showSuiErrorSnackbar(mediaErrorMessage);
    }
  }, [mediaErrorMessage]);

  useEffect(() => {
    if (mediaUploadErrorMessage) {
      showSuiErrorSnackbar(mediaUploadErrorMessage);
    }
  }, [mediaUploadErrorMessage]);

  useEffect(() => {
    currentMediasRef.current = medias;
  }, [medias]);

  useEffect(() => {
    setMediaAltDrafts((currentDrafts) => {
      const nextDrafts: Record<string, string> = {};

      medias.forEach((media) => {
        nextDrafts[media.uuid] =
          currentDrafts[media.uuid] ?? (typeof media.alt === "string" ? media.alt : "");
      });

      return nextDrafts;
    });
  }, [medias]);

  const priceMinPlaceholder =
    priceMode === "FIXED"
      ? serviceEditorTranslations.pricePlaceholder
      : priceMode === "FROM"
        ? serviceEditorTranslations.fromPricePlaceholder
        : serviceEditorTranslations.minimumPricePlaceholder;
  const priceMinFieldLabel =
    priceMode === "FIXED"
      ? serviceEditorTranslations.priceLabel
      : priceMode === "FROM"
        ? serviceEditorTranslations.fromPriceLabel
        : serviceEditorTranslations.minimumPriceLabel;
  const priceMinValidationLabel =
    priceMode === "FIXED"
      ? serviceEditorTranslations.priceLabel
      : serviceEditorTranslations.minimumPriceLabel;
  const pricingGridColumnsDesktop =
    priceMode === "RANGE"
      ? "repeat(5, minmax(0, 1fr))"
      : "repeat(4, minmax(0, 1fr))";
  const deleteServiceTitle =
    title.trim() || serviceEditorTranslations.deleteDialogFallbackTitle;
  const deleteServiceDescription = interpolateTemplate(
    serviceEditorTranslations.deleteDialogDescription,
    {
      title: deleteServiceTitle,
    }
  );

  const applyServiceDetails = (
    service: NonNullable<ReturnType<typeof readCachedServiceByUuid>>
  ) => {
    setTitle(service.title);
    setSlug(service.slug?.trim() ?? "");
    setShortDescription(service.shortDescription ?? "");
    setSeoDescription(service.seoDescription ?? "");
    setDescription(service.description ?? "");
    setPriceMode(service.priceMode);
    setPriceCurrency(service.priceCurrency);
    setPriceMin(String(service.priceMin));
    setPriceMax(typeof service.priceMax === "number" ? String(service.priceMax) : "");
    setPriceUnit(service.priceUnit ?? "");
    setPriceUnitCustom(service.priceUnitCustom ?? "");
    setMedias(sortServiceMedias(service.medias ?? []));
    setCitiesState(createServiceCitiesState(service));
  };

  const handleSameAsProfileChange = (nextSameAsProfile: boolean) => {
    const change = requestSameAsProfileChange(citiesState, nextSameAsProfile);

    if (change.kind === "confirm-reset") {
      setIsCitiesResetDialogOpen(true);
      return;
    }

    setCitiesState(change.state);
  };

  const handleOwnCitiesChange = useCallback((cities: ServiceCity[]) => {
    setCitiesState((currentState) => updateOwnServiceCities(currentState, cities));
  }, []);

  const handleConfirmCitiesReset = () => {
    setCitiesState((currentState) => confirmServiceCitiesReset(currentState));
    setIsCitiesResetDialogOpen(false);
  };

  const visibleCities = resolveVisibleServiceCities(citiesState);

  useEffect(() => {
    let isCancelled = false;

    async function loadService() {
      if (!serviceUuid.trim()) {
        navigate("/services", { replace: true });
        return;
      }

      const cachedData = readCachedServiceByUuid(serviceUuid);
      if (!isCancelled && cachedData !== undefined) {
        if (cachedData) {
          applyServiceDetails(cachedData);
        }
        setIsLoading(false);
      } else if (!isCancelled) {
        setIsLoading(true);
      }

      setErrorMessage("");

      try {
        const service = await fetchServiceByUuid(serviceUuid, accessToken ?? undefined);

        if (!service) {
          if (!isCancelled) {
            setErrorMessage(serviceEditorTranslations.notFound);
            setMedias([]);
          }
          return;
        }

        if (!isCancelled) {
          applyServiceDetails(service);
        }
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : serviceEditorTranslations.loadFailed
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadService();

    return () => {
      isCancelled = true;
    };
  }, [accessToken, navigate, serviceEditorTranslations.loadFailed, serviceEditorTranslations.notFound, serviceUuid]);

  const imageMedias = useMemo(
    () =>
      medias.filter(
        (media) =>
          media.mediaType === "IMAGE" &&
          typeof media.publicUrl === "string" &&
          Boolean(media.publicUrl)
      ),
    [medias]
  );

  const canOpenLightbox = imageMedias.length > 0 && activeLightboxIndex >= 0;
  const normalizedLightboxIndex = canOpenLightbox
    ? (activeLightboxIndex + imageMedias.length) % imageMedias.length
    : 0;
  const detectedMediaTypes = useMemo(
    () => mediaFiles.map((file) => detectMediaTypeFromFile(file)),
    [mediaFiles]
  );
  const undetectedMediaFilesCount = useMemo(
    () => detectedMediaTypes.filter((detectedMediaType) => !detectedMediaType).length,
    [detectedMediaTypes]
  );
  const detectedMediaTypeLabels = useMemo(
    () =>
      Array.from(
        new Set(
          detectedMediaTypes.filter((detectedMediaType): detectedMediaType is ServiceMediaType =>
            Boolean(detectedMediaType)
          )
        )
      ).map((detectedMediaType) => getMediaTypeLabel(detectedMediaType)),
    [detectedMediaTypes]
  );
  const shouldShowMediaTypeSelect = mediaFiles.length > 0 && undetectedMediaFilesCount > 0;
  const mediaPendingRemoval = useMemo(
    () =>
      mediaUuidPendingRemoval
        ? (medias.find((media) => media.uuid === mediaUuidPendingRemoval) ?? null)
        : null,
    [mediaUuidPendingRemoval, medias]
  );

  useEffect(() => {
    if (imageMedias.length === 0 && activeLightboxIndex !== -1) {
      setActiveLightboxIndex(-1);
      return;
    }

    if (activeLightboxIndex >= imageMedias.length && imageMedias.length > 0) {
      setActiveLightboxIndex(imageMedias.length - 1);
    }
  }, [activeLightboxIndex, imageMedias.length]);

  const openLightboxForMedia = (mediaUuid: string) => {
    const mediaIndex = imageMedias.findIndex((media) => media.uuid === mediaUuid);

    if (mediaIndex === -1) {
      return;
    }

    setActiveLightboxIndex(mediaIndex);
  };

  const closeLightbox = () => {
    setActiveLightboxIndex(-1);
  };

  const openAddMediaDialog = (filesToEdit: File[] = []) => {
    setMediaSourceMode("FILE");
    setMediaSourceUrl("");
    setMediaType("");
    setMediaFiles(filesToEdit);
    setMediaUploadErrorMessage("");
    setMediaErrorMessage("");
    setIsAddMediaDialogOpen(true);
  };

  const openAddMediaUrlDialog = () => {
    if (isMediaSubmitting || isMediaUpdating) {
      return;
    }

    setMediaSourceMode("URL");
    setMediaSourceUrl("");
    setMediaType("");
    setMediaFiles([]);
    setMediaUploadErrorMessage("");
    setMediaErrorMessage("");
    setIsAddMediaDialogOpen(true);
  };

  const openQuickAddFilePicker = () => {
    if (isMediaSubmitting || isMediaUpdating) {
      return;
    }

    setMediaUploadErrorMessage("");
    quickAddFileInputRef.current?.click();
  };

  const closeAddMediaDialog = () => {
    if (isMediaSubmitting) {
      return;
    }

    setIsAddMediaDialogOpen(false);
    setMediaSourceMode("FILE");
    setMediaSourceUrl("");
    setMediaType("");
    setMediaFiles([]);
    setMediaUploadErrorMessage("");
    setMediaErrorMessage("");
  };

  const openRemoveMediaDialog = (mediaUuid: string) => {
    if (isMediaSubmitting || isMediaUpdating) {
      return;
    }

    setMediaUuidPendingRemoval(mediaUuid);
  };

  const closeRemoveMediaDialog = () => {
    if (isMediaUpdating) {
      return;
    }

    setMediaUuidPendingRemoval(null);
  };

  const uploadMediaFiles = async (filesToUpload: File[], fallbackMediaType?: ServiceMediaType) => {
    if (filesToUpload.length === 0) {
      return;
    }

    const oversizedFile = filesToUpload.find(
      (fileToUpload) => fileToUpload.size > MAX_MEDIA_UPLOAD_SIZE_BYTES
    );
    if (oversizedFile) {
      const normalizedName = oversizedFile.name.trim();
      const fileLabel = normalizedName ? `"${normalizedName}"` : serviceEditorTranslations.mediaSourceFiles;
      const errorMessage = `${fileLabel} exceeds the size limit. ${
        serviceEditorTranslations.mediaUploadSizeLimit
      }`;
      setMediaUploadErrorMessage(errorMessage);
      setMediaErrorMessage(errorMessage);
      return;
    }

    setMediaUploadErrorMessage("");
    setMediaErrorMessage("");
    setIsMediaSubmitting(true);

    const uploadedMedias: ServiceMedia[] = [];

    try {
      for (const fileToUpload of filesToUpload) {
        const detectedMediaType = detectMediaTypeFromFile(fileToUpload);
        const resolvedMediaType = detectedMediaType ?? fallbackMediaType;

        if (!resolvedMediaType) {
          throw new Error(serviceEditorTranslations.mediaTypeRequired);
        }

        const createdMedia = await uploadServiceMediaRequest(
          { serviceUuid, mediaType: resolvedMediaType, file: fileToUpload },
          accessToken ?? undefined
        );
        uploadedMedias.push(createdMedia);
      }

      setMedias((currentMedias) => sortServiceMedias([...currentMedias, ...uploadedMedias]));
      setIsAddMediaDialogOpen(false);
      setMediaSourceMode("FILE");
      setMediaSourceUrl("");
      setMediaType("");
      setMediaFiles([]);
    } catch (error) {
      if (uploadedMedias.length > 0) {
        setMedias((currentMedias) => sortServiceMedias([...currentMedias, ...uploadedMedias]));
      }

      const normalizedErrorMessage =
        error instanceof Error ? error.message : serviceEditorTranslations.addMediaSubmit;
      setMediaUploadErrorMessage(normalizedErrorMessage);
      setMediaErrorMessage(normalizedErrorMessage);
    } finally {
      setIsMediaSubmitting(false);
    }
  };

  const rehostMediaFromUrl = async (sourceUrl: string, selectedMediaType?: ServiceMediaType) => {
    setMediaUploadErrorMessage("");
    setMediaErrorMessage("");
    setIsMediaSubmitting(true);

    try {
      const createdMedia = await rehostServiceMediaRequest(
        {
          serviceUuid,
          sourceUrl,
          mediaType: selectedMediaType,
        },
        accessToken ?? undefined
      );

      setMedias((currentMedias) => sortServiceMedias([...currentMedias, createdMedia]));
      setIsAddMediaDialogOpen(false);
      setMediaSourceMode("FILE");
      setMediaSourceUrl("");
      setMediaType("");
      setMediaFiles([]);
    } catch (error) {
      const normalizedErrorMessage =
        error instanceof Error ? error.message : serviceEditorTranslations.rehostMediaSubmit;
      setMediaUploadErrorMessage(normalizedErrorMessage);
      setMediaErrorMessage(normalizedErrorMessage);
    } finally {
      setIsMediaSubmitting(false);
    }
  };

  const handleQuickAddFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = "";

    if (selectedFiles.length === 0) {
      return;
    }

    if (!serviceUuid.trim()) {
      setMediaUploadErrorMessage(serviceEditorTranslations.serviceIdentifierMissing);
      return;
    }

    const hasUndetectedMediaType = selectedFiles.some(
      (selectedFile) => !detectMediaTypeFromFile(selectedFile)
    );
    if (hasUndetectedMediaType) {
      setMediaUploadErrorMessage("");
      openAddMediaDialog(selectedFiles);
      return;
    }

    await uploadMediaFiles(selectedFiles);
  };

  const handleAddMedia = async () => {
    if (!serviceUuid.trim()) {
      setMediaErrorMessage(serviceEditorTranslations.serviceIdentifierMissing);
      return;
    }

    if (mediaSourceMode === "URL") {
      const normalizedMediaSourceUrl = mediaSourceUrl.trim();
      if (!normalizedMediaSourceUrl) {
        setMediaErrorMessage(serviceEditorTranslations.mediaUrlRequired);
        return;
      }

      await rehostMediaFromUrl(normalizedMediaSourceUrl, mediaType || undefined);
      return;
    }

    if (mediaFiles.length === 0) {
      setMediaErrorMessage(serviceEditorTranslations.mediaFilesRequired);
      return;
    }

    if (undetectedMediaFilesCount > 0 && !mediaType) {
      setMediaErrorMessage(serviceEditorTranslations.mediaTypeRequired);
      return;
    }

    await uploadMediaFiles(mediaFiles, mediaType || undefined);
  };

  const persistReorderedMedias = async (
    reorderedMedias: ServiceMedia[],
    previousMedias: ServiceMedia[]
  ) => {
    if (!serviceUuid.trim()) {
      setMediaUploadErrorMessage(serviceEditorTranslations.serviceIdentifierMissing);
      setMedias(previousMedias);
      return;
    }

    setMediaUploadErrorMessage("");
    setIsMediaUpdating(true);

    try {
      const persistedMedias = await reorderServiceMediasRequest(
        serviceUuid,
        reorderedMedias.map((media) => media.uuid),
        accessToken ?? undefined
      );

      setMedias(sortServiceMedias(persistedMedias));
    } catch (error) {
      const normalizedErrorMessage =
        error instanceof Error ? error.message : serviceEditorTranslations.mediaDeleteInProgress;
      setMediaUploadErrorMessage(normalizedErrorMessage);
      setMedias(previousMedias);
    } finally {
      setIsMediaUpdating(false);
    }
  };

  const clearDraggedMediaState = () => {
    setDraggedMediaUuid(null);
    setDragOverMediaUuid(null);
    dragStartMediasRef.current = null;
    didDropMediaRef.current = false;
  };

  const handleMediaDragStart = (event: React.DragEvent<HTMLDivElement>, mediaUuid: string) => {
    if (isMediaSubmitting || isMediaUpdating) {
      event.preventDefault();
      return;
    }

    dragStartMediasRef.current = medias;
    didDropMediaRef.current = false;
    setDraggedMediaUuid(mediaUuid);
    setDragOverMediaUuid(null);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", mediaUuid);
  };

  const handleMediaDragOver = (event: React.DragEvent<HTMLDivElement>, mediaUuid: string) => {
    const sourceMediaUuid = draggedMediaUuid || event.dataTransfer.getData("text/plain").trim();

    if (isMediaSubmitting || isMediaUpdating || !sourceMediaUuid) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    if (sourceMediaUuid === mediaUuid) {
      if (dragOverMediaUuid !== mediaUuid) {
        setDragOverMediaUuid(mediaUuid);
      }
      return;
    }

    if (dragOverMediaUuid !== mediaUuid) {
      setDragOverMediaUuid(mediaUuid);
    }

    setMedias((currentMedias) => {
      const sourceMediaIndex = currentMedias.findIndex((media) => media.uuid === sourceMediaUuid);
      const targetMediaIndex = currentMedias.findIndex((media) => media.uuid === mediaUuid);

      if (
        sourceMediaIndex === -1 ||
        targetMediaIndex === -1 ||
        sourceMediaIndex === targetMediaIndex
      ) {
        return currentMedias;
      }

      const nextMedias = moveServiceMedia(currentMedias, sourceMediaIndex, targetMediaIndex);
      currentMediasRef.current = nextMedias;
      return nextMedias;
    });
  };

  const handleMediaDrop = async (
    event: React.DragEvent<HTMLDivElement>,
    targetMediaUuid: string
  ) => {
    event.preventDefault();
    const sourceMediaUuid = draggedMediaUuid || event.dataTransfer.getData("text/plain").trim();
    didDropMediaRef.current = true;

    if (!sourceMediaUuid) {
      return;
    }

    const liveMedias = currentMediasRef.current;
    const sourceMediaIndex = liveMedias.findIndex((media) => media.uuid === sourceMediaUuid);
    const targetMediaIndex = liveMedias.findIndex((media) => media.uuid === targetMediaUuid);
    const reorderedMedias =
      sourceMediaIndex !== -1 && targetMediaIndex !== -1 && sourceMediaIndex !== targetMediaIndex
        ? moveServiceMedia(liveMedias, sourceMediaIndex, targetMediaIndex)
        : liveMedias;

    if (!hasSameMediaOrder(reorderedMedias, liveMedias)) {
      setMedias(reorderedMedias);
    }

    const previousMedias = dragStartMediasRef.current ?? medias;
    if (hasSameMediaOrder(reorderedMedias, previousMedias)) {
      return;
    }

    await persistReorderedMedias(reorderedMedias, previousMedias);
  };

  const handleMediaDragEnd = () => {
    if (!didDropMediaRef.current && dragStartMediasRef.current) {
      setMedias(dragStartMediasRef.current);
    }

    clearDraggedMediaState();
  };

  const handleRemoveMedia = async (mediaUuid: string) => {
    if (isMediaSubmitting || isMediaUpdating) {
      return;
    }

    const previousMedias = medias;
    const nextMedias = medias
      .filter((media) => media.uuid !== mediaUuid)
      .map((media, index) => ({
        ...media,
        sortOrder: index,
      }));

    if (nextMedias.length === medias.length) {
      return;
    }

    setMedias(nextMedias);
    setMediaUploadErrorMessage("");
    setIsMediaUpdating(true);

    try {
      const isRemoved = await removeServiceMediaRequest(mediaUuid, accessToken ?? undefined);
      if (!isRemoved) {
        throw new Error("Unable to remove media. Please try again.");
      }
    } catch (error) {
      const normalizedErrorMessage =
        error instanceof Error ? error.message : "Unable to remove media. Please try again.";
      setMediaUploadErrorMessage(normalizedErrorMessage);
      setMedias(previousMedias);
    } finally {
      setIsMediaUpdating(false);
    }
  };

  const handleConfirmRemoveMedia = async () => {
    if (!mediaUuidPendingRemoval) {
      return;
    }

    const mediaUuid = mediaUuidPendingRemoval;
    setMediaUuidPendingRemoval(null);
    await handleRemoveMedia(mediaUuid);
  };

  const handleMediaAltDraftChange = (mediaUuid: string, nextAltDraft: string) => {
    setMediaAltDrafts((currentDrafts) => ({
      ...currentDrafts,
      [mediaUuid]: nextAltDraft,
    }));
  };

  const handleMediaAltBlur = async (mediaUuid: string, draftAltValue?: string) => {
    if (isMediaSubmitting || isMediaUpdating) {
      return;
    }

    const media = medias.find((mediaItem) => mediaItem.uuid === mediaUuid);
    if (!media) {
      return;
    }

    const draftAlt =
      typeof draftAltValue === "string"
        ? draftAltValue
        : mediaAltDrafts[mediaUuid] ?? "";
    const normalizedDraftAlt = draftAlt.trim();
    const normalizedPersistedAlt = (media.alt ?? "").trim();

    if (normalizedDraftAlt === normalizedPersistedAlt) {
      if (draftAlt !== normalizedPersistedAlt) {
        handleMediaAltDraftChange(mediaUuid, normalizedPersistedAlt);
      }
      return;
    }

    setMediaUploadErrorMessage("");
    setIsMediaUpdating(true);

    try {
      const updatedMedia = await updateServiceMediaRequest(
        {
          serviceUuid,
          uuid: mediaUuid,
          alt: normalizedDraftAlt || null,
        },
        accessToken ?? undefined
      );

      setMedias((currentMedias) =>
        currentMedias.map((currentMedia) =>
          currentMedia.uuid === mediaUuid
            ? {
                ...currentMedia,
                ...updatedMedia,
                sortOrder: currentMedia.sortOrder,
                alt: updatedMedia.alt ?? currentMedia.alt ?? null,
              }
            : currentMedia
        )
      );
      handleMediaAltDraftChange(mediaUuid, updatedMedia.alt ?? "");
    } catch (error) {
      const normalizedErrorMessage =
        error instanceof Error ? error.message : "Unable to update media alt text.";
      setMediaUploadErrorMessage(normalizedErrorMessage);
      handleMediaAltDraftChange(mediaUuid, media.alt ?? "");
    } finally {
      setIsMediaUpdating(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedTitle = title.trim();
    const normalizedSlug = slug.trim();
    const normalizedShortDescription = shortDescription.trim();
    const normalizedSeoDescription = seoDescription.trim();
    const normalizedDescription = description.trim();
    const normalizedPriceUnitCustom = priceUnitCustom.trim();
    const numericPriceMin = Number(priceMin);
    const numericPriceMax = Number(priceMax);

    if (normalizedTitle.length < 2) {
      setErrorMessage(serviceEditorTranslations.titleValidation);
      return;
    }

    if (normalizedSlug.length > 0 && normalizedSlug.length < 3) {
      setErrorMessage(serviceEditorTranslations.slugMin);
      return;
    }

    if (normalizedSlug.length > 128) {
      setErrorMessage(serviceEditorTranslations.slugMax);
      return;
    }

    if (normalizedShortDescription.length > 280) {
      setErrorMessage(serviceEditorTranslations.shortDescriptionMax);
      return;
    }

    if (normalizedSeoDescription.length > 320) {
      setErrorMessage(serviceEditorTranslations.seoDescriptionMax);
      return;
    }

    if (!Number.isFinite(numericPriceMin) || numericPriceMin < 0) {
      setErrorMessage(
        interpolateTemplate(serviceEditorTranslations.priceValidation, {
          label: priceMinValidationLabel,
        })
      );
      return;
    }

    if (priceMode === "RANGE") {
      if (!Number.isFinite(numericPriceMax) || numericPriceMax < 0) {
        setErrorMessage(serviceEditorTranslations.maximumPriceValidation);
        return;
      }

      if (numericPriceMax < numericPriceMin) {
        setErrorMessage(serviceEditorTranslations.maximumPriceRangeValidation);
        return;
      }
    }

    if (priceUnit === "CUSTOM" && !normalizedPriceUnitCustom) {
      setErrorMessage(serviceEditorTranslations.customUnitRequired);
      return;
    }

    if (priceUnit !== "CUSTOM" && normalizedPriceUnitCustom) {
      setErrorMessage(serviceEditorTranslations.customUnitInvalid);
      return;
    }

    const citiesPayloadResult = buildServiceCitiesPayload(citiesState);
    if (!citiesPayloadResult.ok) {
      setErrorMessage(serviceEditorTranslations.citiesRequired);
      return;
    }

    setErrorMessage("");
    setIsSubmitting(true);

    try {
      await updateServiceRequest(
        {
          uuid: serviceUuid,
          title: normalizedTitle,
          slug: normalizedSlug || undefined,
          shortDescription: normalizedShortDescription || undefined,
          seoDescription: normalizedSeoDescription || undefined,
          description: normalizedDescription || undefined,
          priceMode,
          priceCurrency,
          priceMin: numericPriceMin,
          priceMax: priceMode === "RANGE" ? numericPriceMax : undefined,
          priceUnit: priceUnit || undefined,
          priceUnitCustom: normalizedPriceUnitCustom || undefined,
          ...citiesPayloadResult.payload,
        },
        accessToken ?? undefined
      );

      navigate("/services", { replace: true });
    } catch (error) {
      const localizedErrorMessage = resolveLocalizedServiceUpdateErrorMessage(
        error,
        serviceEditorTranslations.slugTaken
      );
      if (localizedErrorMessage) {
        setErrorMessage(localizedErrorMessage);
      } else {
        setErrorMessage(
          error instanceof Error ? error.message : serviceEditorTranslations.updateFailed
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setErrorMessage("");
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (isDeleting) {
      return;
    }

    setIsDeleteDialogOpen(false);
  };

  const handleDeleteService = async () => {
    if (!serviceUuid.trim()) {
      return;
    }

    setErrorMessage("");
    setIsDeleting(true);

    try {
      await deleteServiceRequest(serviceUuid, accessToken ?? undefined);
      navigate("/services", { replace: true });
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : translations.adminServiceEditor.deleteFailed
      );
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {canOpenLightbox ? (
        <Lightbox
          mainSrc={imageMedias[normalizedLightboxIndex]?.publicUrl ?? ""}
          nextSrc={
            imageMedias[(normalizedLightboxIndex + 1) % imageMedias.length]?.publicUrl ?? undefined
          }
          prevSrc={
            imageMedias[(normalizedLightboxIndex + imageMedias.length - 1) % imageMedias.length]
              ?.publicUrl ?? undefined
          }
          imageTitle={imageMedias[normalizedLightboxIndex]?.fileName ?? undefined}
          onCloseRequest={closeLightbox}
          onMovePrevRequest={() => setActiveLightboxIndex((currentIndex) => currentIndex - 1)}
          onMoveNextRequest={() => setActiveLightboxIndex((currentIndex) => currentIndex + 1)}
        />
      ) : null}
      <SoftBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SoftBox mb={2}>
              <SoftTypography variant="h5" fontWeight="medium">
                {serviceEditorTranslations.updateTitle}
              </SoftTypography>
              <SoftTypography variant="button" color="text" fontWeight="regular">
                {serviceEditorTranslations.updateDescription}
              </SoftTypography>
            </SoftBox>
            {isLoading ? (
              <SoftTypography variant="button" color="text">
                {serviceEditorTranslations.loading}
              </SoftTypography>
            ) : (
              <SoftBox component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card>
                      <SoftBox p={3}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Stack spacing={2}>
                              <SoftBox>
                                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                  <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                  >
                                    {serviceEditorTranslations.titleLabel}
                                  </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                  placeholder={serviceEditorTranslations.titlePlaceholder}
                                  value={title}
                                  onChange={(event) => setTitle(event.target.value)}
                                />
                              </SoftBox>
                              <SoftBox>
                                <SoftBox
                                  mb={1}
                                  ml={0.5}
                                  lineHeight={0}
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                >
                                  <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                  >
                                    {serviceEditorTranslations.slugLabel}
                                  </SoftTypography>
                                  <Tooltip
                                    title={serviceEditorTranslations.slugTooltip}
                                    placement="top"
                                    arrow
                                  >
                                    <Icon
                                      fontSize="small"
                                      sx={{
                                        color: "text.secondary",
                                        cursor: "help",
                                        fontSize: "1rem",
                                      }}
                                    >
                                      info
                                    </Icon>
                                  </Tooltip>
                                </SoftBox>
                                <SoftInput
                                  placeholder={serviceEditorTranslations.slugPlaceholder}
                                  value={slug}
                                  onChange={(event) => setSlug(event.target.value)}
                                />
                              </SoftBox>
                              <SoftBox>
                                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                  <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                  >
                                    {serviceEditorTranslations.descriptionLabel}
                                  </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                  placeholder={serviceEditorTranslations.descriptionPlaceholder}
                                  multiline
                                  rows={3}
                                  value={description}
                                  onChange={(event) => setDescription(event.target.value)}
                                />
                              </SoftBox>
                              <SoftBox
                                display="grid"
                                gridTemplateColumns={{
                                  xs: "1fr",
                                  lg: pricingGridColumnsDesktop,
                                }}
                                gap={2}
                                sx={{
                                  width: "100%",
                                  "& .MuiInputBase-root": { width: "100%" },
                                }}
                              >
                                <FormControl fullWidth sx={{ minWidth: 0, width: "100%" }}>
                                  <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                    <SoftTypography
                                      component="label"
                                      variant="caption"
                                      fontWeight="bold"
                                    >
                                      {serviceEditorTranslations.pricingModeLabel}
                                    </SoftTypography>
                                  </SoftBox>
                                  <Select
                                    input={<SoftInput fullWidth />}
                                    value={priceMode}
                                    onChange={(event) => {
                                      const nextPriceMode = event.target.value as ServicePriceMode;
                                      setPriceMode(nextPriceMode);

                                      if (nextPriceMode !== "RANGE") {
                                        setPriceMax("");
                                      }
                                    }}
                                  >
                                    <MenuItem value="FIXED">
                                      {serviceEditorTranslations.pricingModeFixed}
                                    </MenuItem>
                                    <MenuItem value="FROM">
                                      {serviceEditorTranslations.pricingModeFrom}
                                    </MenuItem>
                                    <MenuItem value="RANGE">
                                      {serviceEditorTranslations.pricingModeRange}
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                                <FormControl fullWidth sx={{ minWidth: 0, width: "100%" }}>
                                  <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                    <SoftTypography
                                      component="label"
                                      variant="caption"
                                      fontWeight="bold"
                                    >
                                      {serviceEditorTranslations.currencyLabel}
                                    </SoftTypography>
                                  </SoftBox>
                                  <Select
                                    input={<SoftInput fullWidth />}
                                    value={priceCurrency}
                                    onChange={(event) =>
                                      setPriceCurrency(event.target.value as ServicePriceCurrency)
                                    }
                                  >
                                    {CURRENCY_OPTIONS.map((currencyOption) => (
                                      <MenuItem
                                        key={currencyOption.value}
                                        value={currencyOption.value}
                                      >
                                        {currencyOption.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <FormControl fullWidth sx={{ minWidth: 0, width: "100%" }}>
                                  <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                    <SoftTypography
                                      component="label"
                                      variant="caption"
                                      fontWeight="bold"
                                    >
                                      {priceMinFieldLabel}
                                    </SoftTypography>
                                  </SoftBox>
                                  <SoftInput
                                    fullWidth
                                    type="number"
                                    placeholder={priceMinPlaceholder}
                                    value={priceMin}
                                    onChange={(event) => setPriceMin(event.target.value)}
                                    inputProps={{ min: 0, step: "0.01" }}
                                  />
                                </FormControl>
                                {priceMode === "RANGE" ? (
                                  <FormControl fullWidth sx={{ minWidth: 0, width: "100%" }}>
                                    <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                      <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                      >
                                        {serviceEditorTranslations.maximumPriceLabel}
                                      </SoftTypography>
                                    </SoftBox>
                                    <SoftInput
                                      fullWidth
                                      type="number"
                                      placeholder={serviceEditorTranslations.maximumPricePlaceholder}
                                      value={priceMax}
                                      onChange={(event) => setPriceMax(event.target.value)}
                                      inputProps={{ min: 0, step: "0.01" }}
                                    />
                                  </FormControl>
                                ) : null}
                                <FormControl fullWidth sx={{ minWidth: 0, width: "100%" }}>
                                  <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                    <SoftTypography
                                      component="label"
                                      variant="caption"
                                      fontWeight="bold"
                                    >
                                      {serviceEditorTranslations.unitLabel}
                                    </SoftTypography>
                                  </SoftBox>
                                  <Select
                                    input={<SoftInput fullWidth />}
                                    displayEmpty
                                    value={priceUnit}
                                    onChange={(event) => {
                                      const nextPriceUnit = event.target.value as
                                        | ServicePriceUnit
                                        | "";
                                      setPriceUnit(nextPriceUnit);

                                      if (nextPriceUnit !== "CUSTOM") {
                                        setPriceUnitCustom("");
                                      }
                                    }}
                                    renderValue={(selectedPriceUnit) => {
                                      if (!selectedPriceUnit) {
                                        return serviceEditorTranslations.unitNone;
                                      }

                                      if (selectedPriceUnit === "HOUR") return serviceEditorTranslations.unitHour;
                                      if (selectedPriceUnit === "DAY") return serviceEditorTranslations.unitDay;
                                      if (selectedPriceUnit === "WEEK") return serviceEditorTranslations.unitWeek;
                                      if (selectedPriceUnit === "MONTH") return serviceEditorTranslations.unitMonth;
                                      if (selectedPriceUnit === "SESSION") return serviceEditorTranslations.unitSession;
                                      if (selectedPriceUnit === "ITEM") return serviceEditorTranslations.unitItem;
                                      if (selectedPriceUnit === "CUSTOM") return serviceEditorTranslations.unitCustom;

                                      return String(selectedPriceUnit);
                                    }}
                                  >
                                    <MenuItem value="">{serviceEditorTranslations.unitNone}</MenuItem>
                                    <MenuItem value="HOUR">{serviceEditorTranslations.unitHour}</MenuItem>
                                    <MenuItem value="DAY">{serviceEditorTranslations.unitDay}</MenuItem>
                                    <MenuItem value="WEEK">{serviceEditorTranslations.unitWeek}</MenuItem>
                                    <MenuItem value="MONTH">{serviceEditorTranslations.unitMonth}</MenuItem>
                                    <MenuItem value="SESSION">{serviceEditorTranslations.unitSession}</MenuItem>
                                    <MenuItem value="ITEM">{serviceEditorTranslations.unitItem}</MenuItem>
                                    <MenuItem value="CUSTOM">{serviceEditorTranslations.unitCustom}</MenuItem>
                                  </Select>
                                </FormControl>
                              </SoftBox>
                              {priceUnit === "CUSTOM" ? (
                                <SoftInput
                                  placeholder={serviceEditorTranslations.customUnitPlaceholder}
                                  value={priceUnitCustom}
                                  onChange={(event) => setPriceUnitCustom(event.target.value)}
                                />
                              ) : null}
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Stack spacing={2}>
                              <SoftBox>
                                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                  <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                  >
                                    {serviceEditorTranslations.shortDescriptionLabel}
                                  </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                  placeholder={serviceEditorTranslations.shortDescriptionPlaceholder}
                                  multiline
                                  rows={2}
                                  value={shortDescription}
                                  onChange={(event) => setShortDescription(event.target.value)}
                                />
                              </SoftBox>
                              <SoftBox>
                                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                  <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                  >
                                    {serviceEditorTranslations.seoDescriptionLabel}
                                  </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                  placeholder={serviceEditorTranslations.seoDescriptionPlaceholder}
                                  multiline
                                  rows={4}
                                  value={seoDescription}
                                  onChange={(event) => setSeoDescription(event.target.value)}
                                />
                              </SoftBox>
                              <SoftBox data-testid="service-cities">
                                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                  <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                  >
                                    {serviceEditorTranslations.citiesLabel}
                                  </SoftTypography>
                                </SoftBox>
                                <SoftBox display="flex" alignItems="center" mb={1}>
                                  <Switch
                                    checked={citiesState.sameAsProfile}
                                    onChange={(event) =>
                                      handleSameAsProfileChange(event.target.checked)
                                    }
                                    disabled={isSubmitting || isDeleting}
                                    inputProps={{
                                      "aria-label": serviceEditorTranslations.citiesSameAsProfile,
                                    }}
                                  />
                                  <SoftBox ml={1}>
                                    <SoftTypography
                                      variant="button"
                                      color="text"
                                      fontWeight="regular"
                                    >
                                      {serviceEditorTranslations.citiesSameAsProfile}
                                    </SoftTypography>
                                  </SoftBox>
                                </SoftBox>
                                {citiesState.sameAsProfile ? (
                                  <SoftTypography
                                    variant="button"
                                    color="text"
                                    fontWeight="regular"
                                  >
                                    {visibleCities.length > 0
                                      ? visibleCities.map((city) => city.label).join(", ")
                                      : serviceEditorTranslations.citiesProfileEmpty}
                                  </SoftTypography>
                                ) : (
                                  <CitiesAutocomplete
                                    accessToken={accessToken}
                                    value={visibleCities}
                                    onChange={handleOwnCitiesChange}
                                    disabled={isSubmitting || isDeleting}
                                  />
                                )}
                              </SoftBox>
                            </Stack>
                          </Grid>
                          <Grid item xs={12}>
                            <SoftBox>
                              <input
                                ref={quickAddFileInputRef}
                                type="file"
                                multiple
                                hidden
                                onChange={(event) => {
                                  void handleQuickAddFileChange(event);
                                }}
                              />
                              <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                <SoftTypography
                                  component="label"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  {serviceEditorTranslations.mediaLabel}
                                </SoftTypography>
                              </SoftBox>
                              <SoftBox mt={1.5}>
                                <Grid container spacing={2}>
                                  <Grid item xs={6} sm={4} md={3}>
                                    <SoftBox
                                      display="flex"
                                      flexDirection="column"
                                      alignItems="center"
                                      justifyContent="center"
                                      gap={1}
                                      border="1px dashed"
                                      borderColor="secondary.main"
                                      borderRadius="lg"
                                      py={4}
                                      px={2}
                                      onClick={openQuickAddFilePicker}
                                      sx={{
                                        minHeight: "9rem",
                                        cursor:
                                          isMediaSubmitting || isMediaUpdating
                                            ? "default"
                                            : "pointer",
                                        opacity: isMediaSubmitting || isMediaUpdating ? 0.7 : 1,
                                        transition: "transform 150ms ease, box-shadow 150ms ease",
                                        "&:hover":
                                          isMediaSubmitting || isMediaUpdating
                                            ? undefined
                                            : {
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 6px 16px rgba(15, 23, 42, 0.15)",
                                              },
                                      }}
                                    >
                                      <Icon color="inherit">add_photo_alternate</Icon>
                                        <SoftTypography
                                          variant="caption"
                                          color="secondary"
                                          fontWeight="regular"
                                          textAlign="center"
                                        >
                                          {medias.length === 0
                                          ? serviceEditorTranslations.addMediaEmpty
                                          : serviceEditorTranslations.addMediaAction}
                                        </SoftTypography>
                                    </SoftBox>
                                  </Grid>
                                  <Grid item xs={6} sm={4} md={3}>
                                    <SoftBox
                                      display="flex"
                                      flexDirection="column"
                                      alignItems="center"
                                      justifyContent="center"
                                      gap={1}
                                      border="1px dashed"
                                      borderColor="info.main"
                                      borderRadius="lg"
                                      py={4}
                                      px={2}
                                      onClick={openAddMediaUrlDialog}
                                      sx={{
                                        minHeight: "9rem",
                                        cursor:
                                          isMediaSubmitting || isMediaUpdating
                                            ? "default"
                                            : "pointer",
                                        opacity: isMediaSubmitting || isMediaUpdating ? 0.7 : 1,
                                        transition: "transform 150ms ease, box-shadow 150ms ease",
                                        "&:hover":
                                          isMediaSubmitting || isMediaUpdating
                                            ? undefined
                                            : {
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 6px 16px rgba(15, 23, 42, 0.15)",
                                              },
                                      }}
                                    >
                                      <Icon color="inherit">link</Icon>
                                      <SoftTypography
                                        variant="caption"
                                        color="info"
                                        fontWeight="regular"
                                        textAlign="center"
                                      >
                                        {serviceEditorTranslations.rehostFromLink}
                                      </SoftTypography>
                                    </SoftBox>
                                  </Grid>
                                  {medias.map((media) => {
                                    const canPreview =
                                      media.mediaType === "IMAGE" &&
                                      typeof media.publicUrl === "string" &&
                                      Boolean(media.publicUrl);
                                    const posterUrl =
                                      media.mediaType === "VIDEO" ? media.posterUrl?.trim() || null : null;
                                    const isActionDisabled = isMediaSubmitting || isMediaUpdating;
                                    const isDragSource = draggedMediaUuid === media.uuid;
                                    const isDropTarget =
                                      dragOverMediaUuid === media.uuid &&
                                      draggedMediaUuid !== media.uuid;

                                    return (
                                      <Grid item xs={6} sm={4} md={3} key={media.uuid}>
                                        <SoftBox
                                          borderRadius="lg"
                                          border="1px solid"
                                          borderColor={isDropTarget ? "info.main" : "light.main"}
                                          overflow="hidden"
                                          position="relative"
                                          draggable={!isActionDisabled}
                                          onDragStart={(event) =>
                                            handleMediaDragStart(event, media.uuid)
                                          }
                                          onDragOver={(event) =>
                                            handleMediaDragOver(event, media.uuid)
                                          }
                                          onDrop={(event) => {
                                            void handleMediaDrop(event, media.uuid);
                                          }}
                                          onDragEnd={handleMediaDragEnd}
                                          onClick={
                                            canPreview && !isActionDisabled
                                              ? () => openLightboxForMedia(media.uuid)
                                              : undefined
                                          }
                                          sx={{
                                            minHeight: "9rem",
                                            cursor: isActionDisabled ? "default" : "pointer",
                                            opacity: isDragSource ? 0.7 : 1,
                                            transition:
                                              "transform 150ms ease, box-shadow 150ms ease",
                                            "&:hover":
                                              canPreview && !isActionDisabled
                                                ? {
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 6px 16px rgba(15, 23, 42, 0.15)",
                                                  }
                                                : undefined,
                                          }}
                                        >
                                          <IconButton
                                            size="small"
                                            aria-label={serviceEditorTranslations.removeMediaAria}
                                            disabled={isActionDisabled}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              openRemoveMediaDialog(media.uuid);
                                            }}
                                            sx={{
                                              position: "absolute",
                                              top: 6,
                                              right: 6,
                                              zIndex: 1,
                                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                                              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.15)",
                                              "&:hover": {
                                                backgroundColor: "rgba(255, 255, 255, 1)",
                                              },
                                            }}
                                          >
                                            <Icon fontSize="small">delete</Icon>
                                          </IconButton>
                                          <SoftBox
                                            sx={{
                                              position: "absolute",
                                              top: 8,
                                              left: 8,
                                              zIndex: 1,
                                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                                              borderRadius: "6px",
                                              px: 0.5,
                                              py: 0.25,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              cursor: isActionDisabled ? "default" : "pointer",
                                            }}
                                          >
                                            <Icon fontSize="small" sx={{ color: "text.secondary" }}>
                                              drag_indicator
                                            </Icon>
                                          </SoftBox>
                                          {canPreview ? (
                                            <SoftBox
                                              component="img"
                                              src={media.publicUrl ?? ""}
                                              alt={(media.alt ?? "").trim() || media.fileName}
                                              width="100%"
                                              sx={{
                                                height: "7rem",
                                                display: "block",
                                                objectFit: "cover",
                                              }}
                                            />
                                          ) : posterUrl ? (
                                            <SoftBox position="relative" sx={{ height: "7rem" }}>
                                              <SoftBox
                                                component="img"
                                                src={posterUrl}
                                                alt={(media.alt ?? "").trim() || media.fileName}
                                                width="100%"
                                                sx={{
                                                  height: "7rem",
                                                  display: "block",
                                                  objectFit: "cover",
                                                }}
                                              />
                                              <Icon
                                                sx={{
                                                  position: "absolute",
                                                  top: "50%",
                                                  left: "50%",
                                                  transform: "translate(-50%, -50%)",
                                                  color: "#fff",
                                                  fontSize: "2.5rem !important",
                                                  filter: "drop-shadow(0 1px 4px rgba(0, 0, 0, 0.5))",
                                                }}
                                              >
                                                play_circle
                                              </Icon>
                                            </SoftBox>
                                          ) : (
                                            <SoftBox
                                              display="flex"
                                              justifyContent="center"
                                              alignItems="center"
                                              sx={{ height: "7rem", backgroundColor: "#f8f9fa" }}
                                            >
                                              <Icon color="inherit">
                                                {getMediaIcon(media.mediaType)}
                                              </Icon>
                                            </SoftBox>
                                          )}
                                          <SoftBox p={1}>
                                            <SoftTypography
                                              variant="caption"
                                              color="text"
                                              fontWeight="medium"
                                              sx={{
                                                display: "block",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {media.fileName}
                                            </SoftTypography>
                                            <SoftBox mt={0.75}>
                                              <SoftInput
                                                size="small"
                                                placeholder={serviceEditorTranslations.altTextPlaceholder}
                                                value={mediaAltDrafts[media.uuid] ?? media.alt ?? ""}
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                }}
                                                onMouseDown={(event) => {
                                                  event.stopPropagation();
                                                }}
                                                onChange={(event) => {
                                                  handleMediaAltDraftChange(
                                                    media.uuid,
                                                    event.target.value
                                                  );
                                                }}
                                                onBlur={(event) => {
                                                  void handleMediaAltBlur(
                                                    media.uuid,
                                                    event.target.value
                                                  );
                                                }}
                                                disabled={isActionDisabled}
                                                inputProps={{
                                                  maxLength: 255,
                                                  "aria-label": interpolateTemplate(
                                                    serviceEditorTranslations.altTextAria,
                                                    { fileName: media.fileName }
                                                  ),
                                                }}
                                              />
                                            </SoftBox>
                                          </SoftBox>
                                        </SoftBox>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </SoftBox>
                            </SoftBox>
                          </Grid>
                        </Grid>
                      </SoftBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <SoftBox display="flex" gap={1}>
                      <SoftButton
                        type="submit"
                        variant="gradient"
                        color="info"
                        disabled={
                          isSubmitting ||
                          isDeleting ||
                          !title.trim() ||
                          !priceMin ||
                          (priceMode === "RANGE" && !priceMax) ||
                          isLoading
                        }
                      >
                        {isSubmitting
                          ? serviceEditorTranslations.saving
                          : serviceEditorTranslations.saveChanges}
                      </SoftButton>
                      <SoftButton
                        type="button"
                        variant="outlined"
                        color="error"
                        onClick={handleOpenDeleteDialog}
                        disabled={isSubmitting || isDeleting || isLoading}
                      >
                        {isDeleting
                          ? serviceEditorTranslations.deleteInProgress
                          : serviceEditorTranslations.deleteAction}
                      </SoftButton>
                      <SoftButton
                        type="button"
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate("/services")}
                        disabled={isSubmitting || isDeleting}
                      >
                        {serviceEditorTranslations.cancel}
                      </SoftButton>
                    </SoftBox>
                  </Grid>
                </Grid>
              </SoftBox>
            )}
          </Grid>
        </Grid>
      </SoftBox>
      <Dialog open={isAddMediaDialogOpen} onClose={closeAddMediaDialog} fullWidth maxWidth="sm">
        <DialogTitle>{serviceEditorTranslations.addMediaDialogTitle}</DialogTitle>
        <DialogContent>
          <SoftBox pt={1} display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  {serviceEditorTranslations.mediaSourceLabel}
                </SoftTypography>
              </SoftBox>
              <Select
                input={<SoftInput />}
                value={mediaSourceMode}
                onChange={(event) => {
                  const nextMediaSourceMode = event.target.value as MediaSourceMode;
                  setMediaSourceMode(nextMediaSourceMode);
                  setMediaErrorMessage("");
                  setMediaType("");
                  if (nextMediaSourceMode === "FILE") {
                    setMediaSourceUrl("");
                    return;
                  }

                  setMediaFiles([]);
                }}
              >
                <MenuItem value="FILE">{serviceEditorTranslations.mediaSourceFiles}</MenuItem>
                <MenuItem value="URL">{serviceEditorTranslations.mediaSourceUrl}</MenuItem>
              </Select>
            </FormControl>
            {mediaSourceMode === "FILE" ? (
              <>
                <SoftInput
                  type="file"
                  inputProps={{ accept: "*/*", multiple: true }}
                  onChange={(event) => {
                    const inputElement = event.target as HTMLInputElement;
                    const selectedFiles = Array.from(inputElement.files ?? []) as File[];
                    setMediaFiles(selectedFiles);
                    setMediaErrorMessage("");
                  }}
                />
                {detectedMediaTypeLabels.length > 0 ? (
                  <SoftTypography variant="caption" color="text" fontWeight="regular">
                    Detected media type{detectedMediaTypeLabels.length > 1 ? "s" : ""}:{" "}
                    {detectedMediaTypeLabels.join(", ")}.
                  </SoftTypography>
                ) : null}
                {undetectedMediaFilesCount > 0 ? (
                  <SoftTypography variant="caption" color="text" fontWeight="regular">
                    Could not detect media type for {undetectedMediaFilesCount}{" "}
                    {undetectedMediaFilesCount === 1 ? "file" : "files"}.
                  </SoftTypography>
                ) : null}
              </>
            ) : (
              <>
                <SoftInput
                  placeholder={serviceEditorTranslations.mediaUrlPlaceholder}
                  value={mediaSourceUrl}
                  onChange={(event) => {
                    setMediaSourceUrl(event.target.value);
                    setMediaErrorMessage("");
                  }}
                />
                <SoftTypography variant="caption" color="text" fontWeight="regular">
                  {serviceEditorTranslations.mediaUrlHelper}
                </SoftTypography>
              </>
            )}
            {shouldShowMediaTypeSelect || mediaSourceMode === "URL" ? (
              <FormControl fullWidth>
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    {mediaSourceMode === "URL"
                      ? serviceEditorTranslations.mediaTypeUrlLabel
                      : serviceEditorTranslations.mediaTypeFallbackLabel}
                  </SoftTypography>
                </SoftBox>
                <Select
                  input={<SoftInput />}
                  value={mediaType}
                  onChange={(event) => {
                    setMediaType(event.target.value as ServiceMediaType | "");
                    setMediaErrorMessage("");
                  }}
                >
                  <MenuItem value="" disabled>
                    {serviceEditorTranslations.mediaTypePlaceholder}
                  </MenuItem>
                  {MEDIA_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.value === "IMAGE"
                        ? serviceEditorTranslations.mediaTypeImage
                        : option.value === "VIDEO"
                          ? serviceEditorTranslations.mediaTypeVideo
                          : option.value === "MUSIC"
                            ? serviceEditorTranslations.mediaTypeMusic
                            : serviceEditorTranslations.mediaTypeFile}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <SoftTypography variant="caption" color="text" fontWeight="regular">
              {mediaSourceMode === "URL"
                ? mediaSourceUrl.trim()
                  ? interpolateTemplate(serviceEditorTranslations.selectedUrlTemplate, {
                      value: mediaSourceUrl.trim(),
                    })
                  : serviceEditorTranslations.selectedUrlEmpty
                : mediaFiles.length === 0
                  ? serviceEditorTranslations.mediaFilesRequired
                  : mediaFiles.length === 1
                    ? interpolateTemplate(serviceEditorTranslations.selectedFileTemplate, {
                        value: mediaFiles[0]?.name ?? "",
                      })
                    : interpolateTemplate(serviceEditorTranslations.selectedFilesTemplate, {
                        count: String(mediaFiles.length),
                      })}
            </SoftTypography>
          </SoftBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <SoftButton
            type="button"
            variant="outlined"
            color="secondary"
            onClick={closeAddMediaDialog}
            disabled={isMediaSubmitting}
          >
            {serviceEditorTranslations.cancel}
          </SoftButton>
          <SoftButton
            type="button"
            variant="gradient"
            color="info"
            onClick={handleAddMedia}
            disabled={isMediaSubmitting}
          >
            {isMediaSubmitting
              ? mediaSourceMode === "URL"
                ? serviceEditorTranslations.rehostMediaSubmitting
                : serviceEditorTranslations.addMediaSubmitting
              : mediaSourceMode === "URL"
                ? serviceEditorTranslations.rehostMediaSubmit
                : serviceEditorTranslations.addMediaSubmit}
          </SoftButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(mediaUuidPendingRemoval)}
        onClose={closeRemoveMediaDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{serviceEditorTranslations.mediaDeleteDialogTitle}</DialogTitle>
        <DialogContent>
          <SoftBox pt={1}>
            <SoftTypography variant="button" color="text" fontWeight="regular">
              {interpolateTemplate(serviceEditorTranslations.mediaDeleteDialogDescription, {
                title:
                  mediaPendingRemoval?.fileName ??
                  serviceEditorTranslations.mediaDeleteDialogFallbackTitle,
              })}
            </SoftTypography>
          </SoftBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <SoftButton
            type="button"
            variant="outlined"
            color="secondary"
            onClick={closeRemoveMediaDialog}
            disabled={isMediaUpdating}
          >
            {serviceEditorTranslations.cancel}
          </SoftButton>
          <SoftButton
            type="button"
            variant="gradient"
            color="info"
            onClick={() => {
              void handleConfirmRemoveMedia();
            }}
            disabled={isMediaUpdating || !mediaUuidPendingRemoval}
          >
            {isMediaUpdating
              ? serviceEditorTranslations.mediaDeleteInProgress
              : serviceEditorTranslations.mediaDeleteDialogConfirm}
          </SoftButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isCitiesResetDialogOpen}
        onClose={() => setIsCitiesResetDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{serviceEditorTranslations.citiesResetDialogTitle}</DialogTitle>
        <DialogContent>
          <SoftBox pt={1}>
            <SoftTypography variant="button" color="text" fontWeight="regular">
              {serviceEditorTranslations.citiesResetDialogDescription}
            </SoftTypography>
          </SoftBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <SoftButton
            type="button"
            variant="outlined"
            color="secondary"
            onClick={() => setIsCitiesResetDialogOpen(false)}
          >
            {serviceEditorTranslations.cancel}
          </SoftButton>
          <SoftButton
            type="button"
            variant="gradient"
            color="error"
            onClick={handleConfirmCitiesReset}
          >
            {serviceEditorTranslations.citiesResetDialogConfirm}
          </SoftButton>
        </DialogActions>
      </Dialog>
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>{serviceEditorTranslations.deleteDialogTitle}</DialogTitle>
        <DialogContent>
          <SoftBox pt={1}>
            <SoftTypography variant="button" color="text" fontWeight="regular">
              {deleteServiceDescription}
            </SoftTypography>
          </SoftBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <SoftButton
            type="button"
            variant="outlined"
            color="secondary"
            onClick={handleCloseDeleteDialog}
            disabled={isDeleting}
          >
            {serviceEditorTranslations.deleteDialogCancel}
          </SoftButton>
          <SoftButton
            type="button"
            variant="gradient"
            color="error"
            onClick={() => {
              void handleDeleteService();
            }}
            disabled={isDeleting}
          >
            {isDeleting
              ? serviceEditorTranslations.deleteInProgress
              : serviceEditorTranslations.deleteDialogConfirm}
          </SoftButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default EditService;
